import React, { useState } from 'react';
import { Trophy, Clock, ChevronDown, Gamepad2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Achievement, UserGame } from '../types';

interface ActivityItem {
  type: 'achievement' | 'game_played';
  timestamp: number;
  data: Achievement & { gameName: string; appid: number } | UserGame;
}

interface ActivityFeedProps {
  achievements: Array<Achievement & { gameName: string; appid: number }>;
  recentGames: UserGame[];
}

export function ActivityFeed({ achievements, recentGames }: ActivityFeedProps) {
  const [displayCount, setDisplayCount] = useState(20);
  
  // Create activity items for achievements
  const achievementActivities = achievements.map(achievement => ({
    type: 'achievement' as const,
    timestamp: achievement.unlocktime,
    data: achievement
  }));

  // Create activity items for recent games
  const gameActivities = recentGames.map(game => ({
    type: 'game_played' as const,
    timestamp: Date.now() / 1000 - (game.playtime_2weeks || 0) * 60,
    data: game
  }));

  // Combine and sort all activities
  const activities: ActivityItem[] = [...achievementActivities, ...gameActivities]
    .sort((a, b) => b.timestamp - a.timestamp);

  const displayedActivities = activities.slice(0, displayCount);
  const hasMore = displayCount < activities.length;

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const formatPlaytime = (minutes: number): string => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${remainingMinutes} min`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Activity Feed</h2>
        <span className="text-sm text-gray-500">
          Showing {displayedActivities.length} of {activities.length} activities
        </span>
      </div>
      
      <div className="relative">
        <div className="absolute top-0 bottom-0 left-6 w-px bg-gray-200" />
        <div className="space-y-8">
          {displayedActivities.map((activity, index) => (
            <div key={`${activity.type}-${activity.timestamp}-${index}`} className="relative flex gap-4">
              <div className={`absolute left-0 w-12 h-12 rounded-full bg-white border-4 flex items-center justify-center ${
                activity.type === 'achievement' 
                  ? 'border-yellow-200' 
                  : 'border-indigo-200'
              }`}>
                {activity.type === 'achievement' ? (
                  <Trophy className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Gamepad2 className="w-5 h-5 text-indigo-500" />
                )}
              </div>
              <div className="ml-16 flex-1">
                {activity.type === 'achievement' ? (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Game Header */}
                    <div className="relative h-20 bg-gradient-to-r from-indigo-600 to-indigo-800 overflow-hidden">
                      <img
                        src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${activity.data.appid}/header.jpg`}
                        alt={activity.data.gameName}
                        className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-50"
                      />
                      <div className="absolute inset-0 bg-black/30" />
                      <div className="relative h-full p-4 flex items-center">
                        <img
                          src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${activity.data.appid}/capsule_sm_120.jpg`}
                          alt={activity.data.gameName}
                          className="w-12 h-12 rounded object-cover shadow-lg"
                        />
                        <Link
                          to={`/game/${activity.data.appid}`}
                          className="ml-4 text-lg font-semibold text-white hover:text-indigo-200 transition-colors truncate"
                        >
                          {activity.data.gameName}
                        </Link>
                      </div>
                    </div>
                    {/* Achievement Content */}
                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            <h4 className="font-medium text-gray-900">
                              {activity.data.name}
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            {activity.data.description}
                          </p>
                        </div>
                        {activity.data.icon && (
                          <div className="flex-shrink-0">
                            <img
                              src={activity.data.icon}
                              alt={activity.data.name}
                              className="w-16 h-16 rounded-lg shadow-md"
                            />
                          </div>
                        )}
                      </div>
                      <div className="mt-3 text-sm text-gray-500">
                        {formatTimestamp(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${activity.data.appid}/header.jpg`}
                        alt={activity.data.name}
                        className="w-16 h-16 rounded object-cover"
                      />
                      <div>
                        <Link
                          to={`/game/${activity.data.appid}`}
                          className="font-medium text-gray-900 hover:text-indigo-600 transition-colors"
                        >
                          Played {activity.data.name}
                        </Link>
                        <p className="text-sm text-gray-600">
                          Total playtime: {formatPlaytime(activity.data.playtime_forever)}
                        </p>
                        {activity.data.playtime_2weeks && (
                          <p className="text-sm text-indigo-600">
                            {formatPlaytime(activity.data.playtime_2weeks)} in the past 2 weeks
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      {formatTimestamp(activity.timestamp)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setDisplayCount(prev => prev + 20)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
            <span>Load More</span>
          </button>
        </div>
      )}
    </div>
  );
}